import User from "./user";
import Parameter from "./parameter";
import ModuleCard from "./module-card";
import Entity from "./entity";
import AppSettings from "./app-settings";
import { clone, filter, normalize, sortBy } from "../utils";
import IModuleSearch from "../interface/module-search";
import IModuleCard from "../interface/module-card";
import IModule from "../interface/module";
import ParamStorage from "../enum/param-storage";
import ErrorCode from "../enum/error-code";
import AppSetting from "../enum/app-setting";
import ParameterStorage from "../class/parameter-storage";

class ModuleInstance {

    /* Public Methods */

    public getAll(): IModule[] {
        const modules: IModule[] = [];
        this.getStore().getAll().forEach((module: IModule) => {
            if (User.hasModuleAccess(module.id as number)) {
                modules.push(module);
            }
        });
        this.initDefault(modules);
        this.initOrder(modules);
        this.initCards(modules);
        return modules;
    }

    public get(id: number): IModule | null {
        return this.getAll().find((module: IModule) => module.id === id) || null;
    }

    public getDefault(): IModule {
        const modules: IModule[] = this.getAll();
        return modules.find((mod: IModule) => mod.isDefault) as IModule;
    }

    public async create(mod: IModule): Promise<IModule> {
        if (this.valid(mod)) {
            const result: IModule = await this.getStore().create(this.toRaw(mod));
            if (mod.isDefault) {
                await AppSettings.set(AppSetting.ModuleDefault, result.id as number);
            }
            return result;
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async update(mod: IModule): Promise<IModule> {
        if (this.valid(mod)) {
            if (mod.isDefault) {
                await AppSettings.set(AppSetting.ModuleDefault, mod.id as number);
            } else if (AppSettings.get(AppSetting.ModuleDefault) === mod.id) {
                const modules: IModule[] = this.getAll();
                await AppSettings.set(AppSetting.ModuleDefault, modules[0].id as number);
            }
            return await this.getStore().update(this.toRaw(mod));
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async delete(id: number): Promise<number> {
        const mod: IModule | null = this.get(id);
        if (mod && mod.cards) {
            for (let i = 0; i < mod.cards.length; i++) {
                await ModuleCard.delete(mod.cards[i].id as number);
            }
            return await this.getStore().delete(id);
        }
        return id;
    }

    public async setOrder(order: number[]): Promise<IModule[]> {
        await AppSettings.set(AppSetting.ModuleOrder, order);
        return this.getAll();
    }

    public search(pattern: string, modules: IModule[]): IModuleSearch {
        const ptrn: string = `${normalize(pattern.toLowerCase().trim())}`;
        return {
            modules: clone(modules).filter((m: IModule) => {
                if (m.cards) {
                    m.cards = sortBy(filter(m.cards, ptrn, ['label']), 'label');
                }
                return m.cards && !!m.cards.length;
            }),
            entities: sortBy(filter(Entity.getAllWithIcons(), ptrn, ['singular']), 'singular')
        };
    }

    /* Private Methods */

    private initOrder(modules: IModule[]): void {
        const order: number[] = AppSettings.get(AppSetting.ModuleOrder, []) as number[];
        modules.sort((a: IModule, b: IModule) => {
            let indexA: number = order.indexOf(Number(a.id));
            indexA = indexA === -1 ? modules.length : indexA;
            let indexB: number = order.indexOf(Number(b.id));
            indexB = indexB === -1 ? modules.length : indexB;
            return indexA - indexB;
        });
    }

    private initCards(modules: IModule[]): void {
        const cards: IModuleCard[] = ModuleCard.getAll();
        modules.forEach((module: IModule) => {
            module.cards = cards.filter((card: IModuleCard) => card.moduleId === module.id);
        });
    }

    private initDefault(modules: IModule[]): void {
        let defaultId: number | null = AppSettings.get(AppSetting.ModuleDefault);
        let module: IModule | undefined = modules.find((module: IModule) => module.isDefault = module.id === defaultId);
        if (!defaultId || !module) {
            if (modules[0]) {
                defaultId = modules[0].id as number;
            }
        }
        modules.forEach((module: IModule) => module.isDefault = module.id === defaultId);
    }

    private valid(mod: IModule): boolean {
        mod.name = mod.name.trim();
        return !!mod.name;
    }

    /* Private Methods - Parameter Storage */

    private getStore(): ParameterStorage<IModule> {
        return Parameter.get(ParamStorage.Modules);
    }

    private toRaw(source: IModule): IModule {
        return {
            id: source.id,
            name: source.name,
            icon: source.icon,
            layout: source.layout
        };
    }

}

const Module = new ModuleInstance();

export default Module;